/**
 * route-modules — which routes need a tenant module, and where a landing goes when its
 * module is known to be absent. AppRouter guards direct visits with ROUTE_MODULE; sign-in
 * and the unknown-address page use landingForModules so nobody lands on a page that says
 * it is not part of the workspace. Unknown modules (null) keep every route, as before.
 */
import { can } from "./access";
import { moduleKnownAbsent } from "../lib/tenant-modules";

/** Routes that need a tenant module. Public pages (event.public, hub.public.*, appointment.manage,
 *  queue.join, queue.display) are not listed: they are served to visitors, not Studio users. */
export const ROUTE_MODULE: Readonly<Record<string, string>> = {
  eventday: "events",
  events: "events",
  "events.archived": "events",
  "events.myday": "events",
  "events.mycalendar": "events",
  tickets: "events",
  "ticket-groups": "events",
  "ticket-groups.edit": "events",
  "ticket-types": "events",
  "ticket-types.edit": "events",
  "hub.pages": "events",
  "hub.editor": "events",
  "hub.create.basics": "events",
  "hub.create.page": "events",
  "hub.create.publish": "events",
  "hub.create.live": "events",
  /* The Calendar screens read events only. */
  "calendar.week": "events",
  "calendar.month": "events",
  "calendar.day": "events",
  appointments: "booking",
  "appointment.detail": "booking",
  queues: "queue",
  "queues.console": "queue",
  "queues.health": "queue",
  "queues.dashboard": "queue",
  "queues.boards": "queue",
  "queues.archive": "queue",
};

/** Whether Today's live board is available: it is Workforce data read under wfops.read. */
export function opsBoardAvailable(mods: string[] | null): boolean {
  return can("wfops.read") && !moduleKnownAbsent("workforce", mods);
}

/** True when a route's module is known absent (or, for Today, the board is unavailable). */
export function routeUnavailable(route: string, mods: string[] | null): boolean {
  if (route === "ops.today") return !opsBoardAvailable(mods);
  const needs = ROUTE_MODULE[route];
  return !!needs && moduleKnownAbsent(needs, mods);
}

/** A landing that would open an unavailable page goes to `dashboard` instead, where the
 *  module-aware home picks the events dashboard, queues dashboard, appointments or the welcome page. */
export function landingForModules(route: string, mods: string[] | null): string {
  return routeUnavailable(route, mods) ? "dashboard" : route;
}
